import type { RefreshAcceptedDto } from '@/libs/api-user-mgt/generated/model';
import type { CreateUserInput as GeneratedCreateUserInput } from '@/libs/api-user-mgt/generated/model';
import type { UpdateUserInput as GeneratedUpdateUserInput } from '@/libs/api-user-mgt/generated/model';
import type { UpdateUserDatabasesInput as GeneratedUpdateUserDatabasesInput } from '@/libs/api-user-mgt/generated/model';
import type { UpdateUserPermissionsInput as GeneratedUpdateUserPermissionsInput } from '@/libs/api-user-mgt/generated/model';
import { getApiUserManagementV1AuditLog } from '@/libs/api-user-mgt/generated/audit-log/audit-log';
import { getApiUserManagementV1CustomerDatabaseGroups } from '@/libs/api-user-mgt/generated/customer-database-groups/customer-database-groups';
import { postApiUserManagementV1UsersRefresh } from '@/libs/api-user-mgt/generated/refresh/refresh';
import {
  getApiUserManagementV1Users,
  getApiUserManagementV1UsersUsername,
  getApiUserManagementV1UsersCrossUserIdDatabases,
  patchApiUserManagementV1UsersCrossUserIdDeactivate,
  patchApiUserManagementV1UsersCrossUserIdReactivate,
  postApiUserManagementV1Users,
  postApiUserManagementV1UsersCrossUserIdForcePasswordReset,
  postApiUserManagementV1UsersCrossUserIdLock,
  postApiUserManagementV1UsersCrossUserIdSuspend,
  postApiUserManagementV1UsersCrossUserIdUnlock,
  postApiUserManagementV1UsersCrossUserIdUnsuspend,
  putApiUserManagementV1UsersCrossUserId,
  putApiUserManagementV1UsersCrossUserIdDatabases,
  putApiUserManagementV1UsersCrossUserIdPermissions,
  putApiUserManagementV1UsersCrossUserIdProfile
} from '@/libs/api-user-mgt/generated/users/users';
import { format } from 'date-fns';

import type {
  CreateUserInput,
  DatabasePickerEntry,
  UpdateUserDatabasesInput,
  UpdateUserInput,
  UpdateUserPermissionsInput,
  UpdateUserProfileInput,
  UserDatabasesResponse,
  UserMutationOutcome,
  UserStatusActionInput,
  UserStatusMutationOutcome
} from '@/userManagement/application/types/UserManagementApiContracts';
import type { AuditLogEntry } from '@/userManagement/domain/entities/AuditLogEntry';
import type { UserPermissions } from '@/userManagement/domain/entities/Permission';
import type { PermissionSnapshot } from '@/userManagement/domain/entities/PermissionSnapshot';
import type { User } from '@/userManagement/domain/entities/User';
import type { UsersTabSummary } from '@/userManagement/domain/entities/UsersTabSummary';
import { computeUsersTabSummary } from '@/userManagement/domain/services/usersTabSummary';
import type { PaginatedResponse, UserListParams } from '@/userManagement/domain/types/QueryContracts';

import { USER_MANAGEMENT_DEFAULT_PAGE_SIZE } from '@/userManagement/domain/constants/pagination';
import { axiosInstance } from '@/libs/api-user-mgt/axios-instance';
import { parseFilenameFromContentDisposition } from '@/userManagement/application/utils/triggerBlobDownload';
import { userManagementSnapshotsSegment } from './snapshotsSegment';
import { fetchUserManagementPaged } from './pagination';
import { parseSieveListPayload } from './pagedWire';
import { toUserManagementAuditQueryParams, toUserManagementUsersQueryParams } from './sieveQuery';
import { fetchUserPermissions, fetchUserPermissionsCachedPayload, fetchUserPermissionsForDatabase } from './userPermissionsFetch';
import { mapUserDtoToEntity } from '@/userManagement/application/mappers/userMapper';
import type { UserWireDto } from '@/userManagement/application/types/UserWireDto';
import { mapCrossUserPayloadToWireDto } from '@/userManagement/application/mappers/crossUserListItemMapper';
import { normalizeUserListWireRows } from './userListWire';
import { mergeDatabaseNameLists } from '@/userManagement/application/utils/mergeDatabaseNameLists';
import { parseUserDatabasesResponse } from '@/userManagement/application/utils/parseUserDatabasesResponse';
import { userManagementCatalogSegment } from './catalogSegment';

type PermissionsExportFormat = 'csv' | 'xlsx';

async function downloadPermissionsExportBlob(
  url: string,
  format: PermissionsExportFormat,
  fallbackFilename: string
): Promise<{ blob: Blob; filename: string }> {
  const r = await axiosInstance<Blob>({
    url,
    method: 'GET',
    params: { format },
    responseType: 'blob'
  });

  const disposition = (r.headers['content-disposition'] as string | undefined) ?? (r.headers['Content-Disposition'] as string | undefined);
  const fromHeader = parseFilenameFromContentDisposition(disposition);
  return { blob: r.data, filename: fromHeader ?? fallbackFilename };
}

function aliasForName(name: string, wireEntries: DatabasePickerEntry[], catalogByName: Map<string, string | null>): string | null {
  const fromWire = wireEntries.find((e) => e.name.toLowerCase() === name.toLowerCase())?.alias;
  if (fromWire) return fromWire;
  return catalogByName.get(name.toLowerCase()) ?? null;
}

function buildPickerEntries(
  names: string[],
  wireEntries: DatabasePickerEntry[],
  catalogByName: Map<string, string | null>
): DatabasePickerEntry[] {
  return names.map((name) => ({
    name,
    alias: aliasForName(name, wireEntries, catalogByName)
  }));
}

import { UserManagementGapError, USER_MANAGEMENT_GAP_MESSAGE } from '@/userManagement/domain/errors/UserManagementGapError';
import { parseUserMutationOutcome, parseUserStatusMutationOutcome } from '@/userManagement/application/utils/parseUserMutationOutcome';
/** Satisfies `UserStatusActionInput.justification` min length when the UI does not collect text. */
const UM_STATUS_JUSTIFICATION = 'User account action from User Management UI.';

function buildComparisonWindowLabelFromSnapshots(snapshots: PermissionSnapshot[]): string | undefined {
  if (snapshots.length === 0) return undefined;
  const sorted = [...snapshots].sort((a, b) => a.asOfDate.localeCompare(b.asOfDate));
  const baseline = sorted[0];
  if (!baseline) return undefined;
  return `${baseline.label} → ${format(new Date(), 'MMM d, yyyy')}`;
}

function ensureMinJustification(value: string | undefined, fallback: string, minLen: number): string {
  const v = value?.trim() ?? '';
  if (v.length >= minLen) return v;
  return fallback.length >= minLen ? fallback : `${fallback}${' '.repeat(minLen - fallback.length)}`.slice(0, minLen);
}

export const userManagementUsersSegment = {
  listUsers: (params: UserListParams): Promise<PaginatedResponse<UserWireDto>> =>
    fetchUserManagementPaged<UserWireDto>('users', params as Record<string, unknown>),

  createUser: async (data: CreateUserInput, idempotencyKey: string): Promise<UserMutationOutcome> => {
    const body: GeneratedCreateUserInput = {
      username: data.username.trim(),
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      primaryEmail: data.email.trim(),
      primaryPhone: data.phone?.trim() ? data.phone.trim() : null,
      permissionRoleId: data.permissionRoleId,
      databases: data.assignedDatabases,
      ...(data.justification?.trim() ? { justification: data.justification.trim() } : {})
    };
    const r = await postApiUserManagementV1Users(body, {
      headers: { 'X-Idempotency-Key': idempotencyKey }
    });
    return parseUserMutationOutcome(r.data, r.status);
  },

  updateUserProfile: async (crossUserId: string, data: UpdateUserProfileInput, idempotencyKey: string): Promise<UserMutationOutcome> => {
    const body = {
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      primaryEmail: data.email.trim(),
      primaryPhone: data.phone?.trim() ? data.phone.trim() : null,
      ...(data.justification?.trim() ? { justification: data.justification.trim() } : {})
    };
    const r = await putApiUserManagementV1UsersCrossUserIdProfile(crossUserId, body, {
      headers: { 'X-Idempotency-Key': idempotencyKey }
    });
    return parseUserMutationOutcome(r.data, r.status);
  },

  deactivateUser: async (crossUserId: string, data: UserStatusActionInput, idempotencyKey: string): Promise<UserStatusMutationOutcome> => {
    const justification = ensureMinJustification(data.justification, 'User deactivation from User Management UI.', 10);
    const r = await patchApiUserManagementV1UsersCrossUserIdDeactivate(
      crossUserId,
      { justification },
      { headers: { 'X-Idempotency-Key': idempotencyKey } }
    );
    return parseUserStatusMutationOutcome(r.data, r.status, { appliedIsActive: false });
  },

  reactivateUser: async (crossUserId: string, data: UserStatusActionInput, idempotencyKey: string): Promise<UserStatusMutationOutcome> => {
    const justification = ensureMinJustification(data.justification, 'User reactivation from User Management UI.', 10);
    const r = await patchApiUserManagementV1UsersCrossUserIdReactivate(
      crossUserId,
      { justification },
      { headers: { 'X-Idempotency-Key': idempotencyKey } }
    );
    return parseUserStatusMutationOutcome(r.data, r.status, { appliedIsActive: true });
  },
  /** POST /users/refresh — server rebuilds user catalog; pass optional SignalR `connectionId` when the API scopes refresh to a hub instance. */
  refreshUsersCatalog: async (connectionId?: string): Promise<RefreshAcceptedDto> => {
    const trimmed = connectionId?.trim();
    const r = await postApiUserManagementV1UsersRefresh(trimmed != null && trimmed.length > 0 ? { connectionId: trimmed } : undefined);
    return r.data;
  },

  getUser: async (crossUserId: string, username: string): Promise<User> => {
    const u = username.trim();
    if (u === '') {
      throw new Error('getUser: username is required for User Management reads (GET /users/{username}).');
    }
    const r = await getApiUserManagementV1UsersUsername(u);
    const wire = mapCrossUserPayloadToWireDto(r.data);
    if (!wire) {
      throw new Error('getUser: empty or unrecognized user payload.');
    }
    if (String(wire.id).trim() !== String(crossUserId).trim()) {
      console.warn('[userManagement] getUser: response id does not match requested crossUserId', {
        requestedCrossUserId: crossUserId,
        responseId: wire.id
      });
    }
    return mapUserDtoToEntity(wire);
  },

  updateUser: async (crossUserId: string, username: string, data: UpdateUserInput): Promise<User> => {
    const body: GeneratedUpdateUserInput = {
      firstName: data.firstName,
      lastName: data.lastName,
      primaryEmail: data.email,
      primaryPhone: data.phone,
      permissionRoleId: data.permissionRoleId ?? null
    };
    await putApiUserManagementV1UsersCrossUserId(crossUserId, body);
    return userManagementUsersSegment.getUser(crossUserId, username);
  },

  getUserPermissions: (crossUserId: string, username: string, database?: string) => fetchUserPermissions(username, crossUserId, database),

  getUserPermissionsCachedPayload: (crossUserId: string, username: string) => fetchUserPermissionsCachedPayload(username, crossUserId),

  getUserPermissionsForDatabase: (crossUserId: string, databaseName: string, username: string) =>
    fetchUserPermissionsForDatabase(crossUserId, databaseName, username),

  updateUserPermissions: async (crossUserId: string, username: string, data: UpdateUserPermissionsInput): Promise<UserPermissions> => {
    const permissions = data.permissions.map((p) => ({
      featureId: p.featureId,
      allowRead: p.allowRead,
      allowWrite: p.allowWrite,
      allowDelete: p.allowDelete,
      allowCentralAccess: p.allowCentralAccess,
      allowExcelExport: p.allowExcelExport
    }));
    const body: GeneratedUpdateUserPermissionsInput = {
      dbName: data.targetDatabases?.[0] ?? null,
      permissions,
      justification: ensureMinJustification(data.justification, 'Permission matrix save via User Management.', 10),
      changeCategory: data.changeCategory?.trim() ? data.changeCategory : 'General'
    };
    await putApiUserManagementV1UsersCrossUserIdPermissions(crossUserId, body);
    const dbAfter = data.targetDatabases?.[0]?.trim() ?? '';
    if (dbAfter.length > 0) {
      return fetchUserPermissionsForDatabase(crossUserId, dbAfter, username);
    }
    return fetchUserPermissions(username, crossUserId);
  },

  getUserDatabases: async (crossUserId: string, username: string): Promise<UserDatabasesResponse> => {
    const u = username.trim();
    if (u === '') {
      throw new Error('getUserDatabases: username is required (GET /users/{username}).');
    }
    const [userRes, groupsRes, assignRes, catalogRecords] = await Promise.all([
      getApiUserManagementV1UsersUsername(u),
      getApiUserManagementV1CustomerDatabaseGroups(),
      getApiUserManagementV1UsersCrossUserIdDatabases(crossUserId).catch(() => null),
      userManagementCatalogSegment.getDatabases().catch(() => [])
    ]);
    const wire = mapCrossUserPayloadToWireDto(userRes.data);
    if (!wire) {
      throw new Error('getUserDatabases: user payload could not be mapped (GET /users/{username}).');
    }
    const user = mapUserDtoToEntity(wire);
    const fromUser = user.assignedDatabases ?? [];
    const dedicatedParsed = assignRes != null ? parseUserDatabasesResponse(assignRes.data) : parseUserDatabasesResponse(null);
    const fromDedicated = dedicatedParsed.assignedDatabases ?? [];
    const assigned = mergeDatabaseNameLists(fromUser, fromDedicated, dedicatedParsed.availableDatabases);
    const groupsParsed = parseSieveListPayload<Record<string, unknown>>(groupsRes.data, groupsRes.headers, 0);
    const allNames = groupsParsed.rows
      .map((g) => String(g.groupName ?? g.GroupName ?? g.name ?? g.databaseName ?? g.dbName ?? g.label ?? ''))
      .filter((n) => n.trim() !== '');
    const availableFromDedicated = dedicatedParsed.availableDatabases ?? [];
    const availableFromGroups = allNames.filter((n) => !assigned.some((a) => a.toLowerCase() === n.toLowerCase()));
    const available = mergeDatabaseNameLists(availableFromDedicated, availableFromGroups);

    const catalogByName = new Map(catalogRecords.map((r) => [r.name.toLowerCase(), r.alias ?? null]));
    const wireEntries = [...dedicatedParsed.assignedEntries, ...dedicatedParsed.availableEntries];
    if (user.tenantDatabaseEntries?.length) {
      for (const entry of user.tenantDatabaseEntries) {
        wireEntries.push({ name: entry.tenantDatabaseName, alias: entry.databaseAlias ?? null });
      }
    }

    return {
      assignedDatabases: assigned,
      availableDatabases: available,
      assignedEntries: buildPickerEntries(assigned, wireEntries, catalogByName),
      availableEntries: buildPickerEntries(available, wireEntries, catalogByName)
    };
  },

  updateUserDatabases: async (crossUserId: string, data: UpdateUserDatabasesInput): Promise<void> => {
    const body: GeneratedUpdateUserDatabasesInput = {
      databases: data.assignedDatabases,
      justification: ensureMinJustification(data.justification, 'Database assignment updated via User Management UI.', 10),
      changeCategory: data.changeCategory?.trim() ? data.changeCategory : 'General'
    };
    await putApiUserManagementV1UsersCrossUserIdDatabases(crossUserId, body);
  },

  lockUser: (crossUserId: string): Promise<void> =>
    postApiUserManagementV1UsersCrossUserIdLock(crossUserId, { justification: UM_STATUS_JUSTIFICATION }).then(() => undefined),

  unlockUser: (crossUserId: string): Promise<void> =>
    postApiUserManagementV1UsersCrossUserIdUnlock(crossUserId, { justification: UM_STATUS_JUSTIFICATION }).then(() => undefined),

  suspendUser: (crossUserId: string): Promise<void> =>
    postApiUserManagementV1UsersCrossUserIdSuspend(crossUserId, { justification: UM_STATUS_JUSTIFICATION }).then(() => undefined),

  unsuspendUser: (crossUserId: string): Promise<void> =>
    postApiUserManagementV1UsersCrossUserIdUnsuspend(crossUserId, { justification: UM_STATUS_JUSTIFICATION }).then(() => undefined),

  forcePasswordReset: (crossUserId: string): Promise<void> =>
    postApiUserManagementV1UsersCrossUserIdForcePasswordReset(crossUserId, { justification: UM_STATUS_JUSTIFICATION }).then(
      () => undefined
    ),

  clearForceNewPassword: async (_crossUserId: string): Promise<void> => {
    throw new UserManagementGapError(USER_MANAGEMENT_GAP_MESSAGE.clearForceNewPassword, 'clearForceNewPassword');
  },

  /**
   * Single-user permission matrix export by CrossUserId —
   * GET `/api/user-management/v1/users/{crossUserId}/permissions/export`.
   */
  exportUserPermissions: async (crossUserId: string, format: PermissionsExportFormat): Promise<{ blob: Blob; filename: string }> =>
    downloadPermissionsExportBlob(
      `/api/user-management/v1/users/${encodeURIComponent(crossUserId)}/permissions/export`,
      format,
      `user-permissions-${crossUserId}.${format}`
    ),

  /**
   * Single-user permission matrix export by username (all tenant DBs) —
   * GET `/api/user-management/v1/users/{username}/permissions/export`.
   */
  exportUserPermissionsByUsername: async (username: string, format: PermissionsExportFormat): Promise<{ blob: Blob; filename: string }> =>
    downloadPermissionsExportBlob(
      `/api/user-management/v1/users/${encodeURIComponent(username)}/permissions/export`,
      format,
      `user-permissions-${username}.${format}`
    ),

  /**
   * All-users permission matrix export for one database —
   * GET `/api/user-management/v1/cross-user-permissions/export`.
   */
  exportCrossUserPermissions: async (databaseName: string, format: PermissionsExportFormat): Promise<{ blob: Blob; filename: string }> => {
    const r = await axiosInstance<Blob>({
      url: '/api/user-management/v1/cross-user-permissions/export',
      method: 'GET',
      params: { databaseName, format },
      responseType: 'blob'
    });

    const disposition =
      (r.headers['content-disposition'] as string | undefined) ?? (r.headers['Content-Disposition'] as string | undefined);
    const fromHeader = parseFilenameFromContentDisposition(disposition);
    return { blob: r.data, filename: fromHeader ?? `user-permissions-${databaseName}.${format}` };
  },

  getUsersTabSummary: async (): Promise<UsersTabSummary> => {
    const pageSize = USER_MANAGEMENT_DEFAULT_PAGE_SIZE;
    const userParams = toUserManagementUsersQueryParams({ page: 1, pageSize });
    const auditParams = toUserManagementAuditQueryParams({ page: 1, pageSize });
    // TROC-281 / TROC-237: deviation count must match the Role-deviation filter (full BE population),
    // not the first-page unfiltered slice used for other summary cards.
    const deviationCountParams = toUserManagementUsersQueryParams({
      page: 1,
      pageSize: 1,
      hasRoleDeviation: true
    });
    const [usersRes, auditRes, snapshots, deviationCountRes] = await Promise.all([
      getApiUserManagementV1Users(userParams),
      getApiUserManagementV1AuditLog(auditParams),
      userManagementSnapshotsSegment.listSnapshots().catch((): PermissionSnapshot[] => []),
      getApiUserManagementV1Users(deviationCountParams)
    ]);
    const usersParsed = parseSieveListPayload<unknown>(usersRes.data, usersRes.headers, pageSize);
    const auditParsed = parseSieveListPayload<unknown>(auditRes.data, auditRes.headers, pageSize);
    const deviationParsed = parseSieveListPayload<unknown>(deviationCountRes.data, deviationCountRes.headers, 1);
    const users = normalizeUserListWireRows(usersParsed.rows as unknown[]).map((w) => mapUserDtoToEntity(w));
    const auditPayload = auditParsed.rows as unknown as AuditLogEntry[];
    const summary = computeUsersTabSummary(users, auditPayload, {
      usersWithRoleDeviationCount: deviationParsed.totalCount
    });
    return {
      ...summary,
      comparisonWindowLabel: buildComparisonWindowLabelFromSnapshots(snapshots)
    };
  }
};
